import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from '@providers/MediaQueryProvider';
import { useTranslate } from '@providers/TranslateProvider';
import { useLocation } from '@tanstack/react-router';

import { ChessBoard } from '@exports/ChessBoard';
import { useBoardTheme } from '@exports/hooks/useBoardTheme';
import { Scoresheet } from '@exports/Scoresheet';
import { OBJECT_EMPTY } from '@libs/constants';

import { useAuth } from '@features/auth/hooks/useAuth';
import { TIME_PRESSURE } from '@features/game/constants';
import { useAutoPromotion } from '@features/game/hooks/useAutoPromotion';
import { useGameConflictModal } from '@features/game/hooks/useGameConflictModal';
import { useGameErrorToast } from '@features/game/hooks/useGameErrorToast';
import { useGameServer } from '@features/game/hooks/useGameServer';
import { useGameSubscription } from '@features/game/hooks/useGameSubscription';
import { useRotateBoard } from '@features/game/hooks/useRotateBoard';
import { useScoresheet } from '@features/game/hooks/useScoresheet';
import { IGame } from '@features/game/types/game';
import { DrawOffer } from '@features/game/ui/DrawOffer';
import { GameConflictModal } from '@features/game/ui/GameConflictModal';
import { GameInfo } from '@features/game/ui/GameInfo/GameInfo';
import { GameLayout, GameLayoutSkeleton } from '@features/game/ui/GameLayout/GameLayout';
import { MatchCountdownWidget } from '@features/game/ui/MatchCountdown';
import { NetworkProblems } from '@features/game/ui/NetworkProblems/NetworkProblems';
import { PlayerPanelWidget } from '@features/game/ui/PlayerPanel';
import { formatGameSpecs } from '@features/game/utils/formatGameSpecs';
import {
  downloadServerGamePgn,
  getGamePgnFilename,
  mapGameToPgn,
} from '@features/game/utils/gamePgnUtils';
import { GAME_STREAM_SOCKET_URL, useStreamsGameQuery } from '@features/shared/api/streams';
import { useNoPairModal } from '@features/tournament/hooks/useNoPairModal';
import { NoPairModal } from '@features/tournament/ui/components/NoPairModal';
import { UserGameMenuWidget } from '@features/user/ui/UserGameMenu/UserGameMenuWidget';
import { useVideoStreamCtx } from '@features/video/providers';

import { Analysis } from '../AnalysisPage';
import { GameActions } from './components/GameActions/GameActions';

import styles from './GamePageWidget.module.css';

/*
    ____,-------------------------------,____
    \   |              Page             |   /
    /___|-------------------------------|___\
*/

type Props = {
  key: string /* sic! Для разных игр необходимы разные ключи */;
  tournamentId: string;
  gameId: string;
  onLeave?: () => void;
};

export function GamePageWidget({ tournamentId, gameId, onLeave }: Props) {
  const { lang, t } = useTranslate();
  const mobile = useMediaQuery() !== 'desktop';
  const { boardTheme } = useBoardTheme();

  const customBackUrl = useLocation().state.backUrl;
  const onBack = useCallback(() => history.back(), []);

  const initData = useInitialGameInfo(gameId);
  const { autoPromotion } = useAutoPromotion();

  const { toast, showToast } = useGameErrorToast();
  const handleError = useCallback(
    (_error: Error) => {
      showToast(
        <>
          <b>{t('@features/game/pages/GamePage/Error Header')}</b>.{' '}
          {t('@features/game/pages/GamePage/Error Text')}
        </>,
      );
    },
    [showToast, t],
  );

  const subscription = useGameSubscription({
    gameId,
    customSocketUrl: initData.customSocketUrl,
    enabled: initData.isActive,
  });

  const {
    loading,
    networkPromlems,
    //
    propsBoard,
    //
    game,
    side,
    //
    isSecondPlayer,
    //
    canRequestDraw,
    showDrawOffer,
    //
    handleOfferDraw,
    handleAcceptDraw,
    handleDeclineDraw,
    handleResign,
  } = useGameServer({
    gameId,
    playerId: initData.playerId,
    isPlaying: initData.isPlaying,
    autoPromotion,
    subscription,
    onError: handleError,
  });

  const isInProgress = game?.status === 'InProgress';
  const isFinished = game?.status === 'Finished';
  const isNotStarted = game?.status === 'NotStarted';

  const { rotated, setRotated, remapColor, invertColor } = useRotateBoard();
  const onRotate = useCallback(() => setRotated((r) => !r), [setRotated]);

  // Видеоконтроль участника; с этого устройства шла трансляция — сразу показываем ее
  const video = useVideoStreamCtx();
  const hasVideo = !!video?.visible;
  const refVideoShown = useRef(false);
  useEffect(() => {
    if (refVideoShown.current || !video?.visible || video.source !== 'device') return;
    refVideoShown.current = true;
    video.openModal();
  }, [video]);

  const title = formatTitle(game, t);
  const gameSpecs = useMemo(
    () => formatGameSpecs(game, remapColor(propsBoard.orientation), t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [game?.id, propsBoard.orientation, lang],
  );

  const scoresheet = useScoresheet({
    game,
    boardProps: propsBoard,
    autoResetOnMove: initData.isPlaying,
  });
  const customPlayersTimeMs =
    !scoresheet.isLastMove || isFinished ? scoresheet.playersTimeMs : undefined;

  const modal = useModal(tournamentId, gameId, networkPromlems, game);

  const onDownloadPgn = useCallback(() => game && downloadServerGamePgn(game), [game]);

  const [showAnalysis, setShowAnalysis] = useState<boolean | null>(null);
  const onOpenAnalysis = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    setShowAnalysis(true);
  }, []);
  const onCloseAnalysis = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    setShowAnalysis(false);
  }, []);

  // TODO: добавить обработку ошибок
  if (initData.loading || loading || !game) {
    return <GameLayoutSkeleton />;
  }

  const { orientation: play } = propsBoard;

  const fullTurnHappened = game.moves.length >= 2;

  if (showAnalysis) {
    return (
      <Analysis
        avatar={<UserGameMenuWidget />}
        initialPgn={mapGameToPgn(game)}
        filename={getGamePgnFilename(game)}
        initialOrientation={side !== 'none' ? side : 'white'}
        isRealGameAnalysis
        onClose={onCloseAnalysis}
      />
    );
  }

  return (
    <GameLayout
      backLink={customBackUrl}
      onBackClick={customBackUrl ? onBack : undefined}
      board={
        <ChessBoard
          key={rotated ? 'r' : 'o'}
          theme={boardTheme}
          {...propsBoard}
          {...scoresheet.propsPatch}
          {...(modal ? { modal } : OBJECT_EMPTY)}
          notation={mobile ? 'inside' : undefined}
          noPieces={game.isBlind && isInProgress}
          noWaypoints={game.isBlind && isInProgress}
          className={
            rotated !== null || showAnalysis !== null ? styles.is_rotate_animated : undefined
          }
          orientation={remapColor(play)}
          onRotate={mobile ? undefined : onRotate}
        />
      }
      avatar={<UserGameMenuWidget />}
      header={
        <GameInfo
          title={title}
          specs={gameSpecs}
          mobileBackLink={customBackUrl}
          onMobileBackClick={customBackUrl ? onBack : undefined}
        />
      }
      errorToast={toast}
      popup={
        showDrawOffer && initData.playerId ? (
          <DrawOffer onAccept={handleAcceptDraw} onDecline={handleDeclineDraw} />
        ) : undefined
      }
      player1={
        <PlayerPanelWidget
          orientation="top"
          color={invertColor(remapColor(play))}
          game={game}
          mobile={mobile}
          timePressureSec={TIME_PRESSURE}
          scoresheetTimeMs={customPlayersTimeMs}
        />
      }
      player2={
        <PlayerPanelWidget
          orientation="bottom"
          color={remapColor(play)}
          game={game}
          mobile={mobile}
          timePressureSec={TIME_PRESSURE}
          scoresheetTimeMs={customPlayersTimeMs}
        />
      }
      scoresheet={<Scoresheet {...scoresheet.props} enableGlobalShortcuts />}
      actions={
        <GameActions
          isPlaying={initData.isPlaying && (isInProgress || isNotStarted)}
          isRotated={!!rotated}
          system={game.tournament?.system}
          canRequestDraw={canRequestDraw}
          canResign={initData.isPlaying && !isSecondPlayer && fullTurnHappened}
          canAnalyze={isFinished}
          canDownloadPgn={isFinished}
          onRotate={mobile ? onRotate : undefined}
          videoSource={hasVideo ? video.source : undefined}
          videoLive={hasVideo && video.status === 'live'}
          videoMicOff={hasVideo && video.micOff}
          videoCamOff={hasVideo && video.camOff}
          videoError={hasVideo && video.error}
          videoActive={hasVideo && video.isModalOpen}
          onVideo={hasVideo ? video.toggleModal : undefined}
          onDraw={handleOfferDraw}
          onResign={handleResign}
          onDownload={onDownloadPgn}
          onLeave={onLeave}
          onAnalize={onOpenAnalysis}
        />
      }
    />
  );
}

/*
    ____,-------------------------------,____
    \   |              Hooks            |   /
    /___|-------------------------------|___\
*/

function useInitialGameInfo(gameId: string) {
  const userId = useAuth().user?.userId;
  const { data, isPending } = useStreamsGameQuery(gameId);

  const isPlayer =
    userId &&
    (data?.white.id === userId ||
      data?.black.id === userId ||
      data?.whiteSecondPlayer?.id === userId ||
      data?.blackSecondPlayer?.id === userId);
  const isActive = data?.status === 'InProgress' || data?.status === 'NotStarted';

  const isPlaying = Boolean(isPlayer && isActive);

  return {
    loading: isPending,
    playerId: isPlayer ? userId : undefined,
    isPlaying,
    isActive,
    customSocketUrl: isPlaying ? undefined : GAME_STREAM_SOCKET_URL,
  };
}

function useModal(tournamentId: string, gameId: string, networkPromlems: boolean, game?: IGame) {
  // Модалка "Для вас не нашлось пары"
  const noPairModal = useNoPairModal(tournamentId);
  // Модалка "Начинается партия в другом турнире"
  const gameConflict = useGameConflictModal(gameId);
  // Выбор модалки
  const modal = (function () {
    if (gameConflict) return <GameConflictModal {...gameConflict} />;
    if (noPairModal) return <NoPairModal {...noPairModal} />;
    if (networkPromlems) return <NetworkProblems />;
    if (game?.result === 'NotStarted') {
      return <MatchCountdownWidget game={game} />;
    }
  })();

  return modal;
}

/*
    ____,-------------------------------,____
    \   |              Utils            |   /
    /___|-------------------------------|___\
*/

function formatTitle(game: IGame | undefined, t: ReturnType<typeof useTranslate>['t']) {
  if (!game?.tournament) return 'Игра';

  const round = game.round?.roundNumber
    ? `. ${t('@features/game/pages/GamePage/Round No')}\u00A0${game.round?.roundNumber}`
    : null;
  return `${game.tournament.name.replace(/\.$/, '')}${round ?? ''}`;
}
